'use client';

/**
 * Improve Assistant — an agentic chat for the continuous improvement loop.
 *
 * Same machinery as the SEO/Ads assistants: a dedicated gateway session
 * (`improve-asst`) opened with cwd `~/beast`, framed by the shared
 * AssistantShell. This file only supplies the improve-specific identity
 * and grounding.
 *
 * What makes it different from the read-only assistants: it can FILE new
 * recommendations into the approval inbox via the authed
 * `POST /api/improve/recommendations` route (it reads GATEWAY_TOKEN from
 * ~/beast/.env). It can also trigger the SEO CTR analyzer. It can NOT
 * approve or execute anything — that stays a human click in the inbox
 * (the loop's iron rule).
 */
import { AssistantShell } from '@/components/chat/AssistantShell';
import type { ImproveRecommendation } from '@/components/improve/RecommendationCard';

interface OverviewLite {
  byStatus: Record<string, number>;
  byBusiness: Array<{ business: string; proposed: number; approved: number; total: number }>;
  executedThisWeek: number;
}

const SUGGESTIONS = [
  'What should I improve this week? Look across all sites and file the best opportunities.',
  'Walk me through the pending recommendations — which are worth approving?',
  'Run the CTR analyzer on my best-traffic site and summarize what it finds.',
  'Which executed changes actually worked?',
];

function buildGrounding(overview: OverviewLite | null, recs: ImproveRecommendation[]): string {
  const L: string[] = [];
  L.push(`You are the Improve Assistant inside the Beast dashboard — the copilot for the continuous improvement loop (MEASURE → ANALYZE → PROPOSE → human approves → EXECUTE). You advise on and file concrete improvement recommendations for the user's businesses. Be concise and specific: exact pages, exact keywords, exact numbers, short markdown.`);
  L.push('');
  L.push('## Current inbox state');
  if (overview) {
    const s = overview.byStatus || {};
    L.push(`- Proposed: ${s.proposed || 0}, Approved (awaiting execute): ${s.approved || 0}, Executed this week: ${overview.executedThisWeek || 0}`);
    for (const b of overview.byBusiness || []) {
      L.push(`- ${b.business}: ${b.proposed} pending / ${b.total} total`);
    }
  } else {
    L.push('- (overview not loaded — fetch it yourself)');
  }
  if (recs.length) {
    L.push('');
    L.push('## Recommendations currently on screen');
    for (const r of recs.slice(0, 15)) {
      L.push(`- [${r.id}] ${r.business} · ${r.type} · ${r.status} — ${r.title}`);
    }
  }
  L.push('');
  L.push('## Your tools (curl the local Beast API, base http://localhost:3000)');
  L.push('- `GET /api/improve/overview` — counts by status/business.');
  L.push('- `GET /api/improve/recommendations?business=&status=&type=` — full detail incl. evidence, proposal, latest execution.');
  L.push('- `GET /api/seo/domains` — the GSC-tracked sites. `GET /api/seo/<domain>` — top keywords w/ position+CTR, pages, movers, devices, history.');
  L.push('- `POST /api/improve/analyze` body `{"business":"<domain>"}` — runs the SEO CTR analyzer for one site (takes a few minutes; tell the user before running).');
  L.push('- **File a recommendation**: `POST /api/improve/recommendations` with header `Authorization: Bearer $GATEWAY_TOKEN` (read the token from `~/beast/.env`, never print it). JSON body: `business` (domain slug), `type` (one of seo_title|seo_meta|content_gap|ads_budget|ads_creative|ads_pause|ab_promote|reel_idea), `title` (one-line summary), `evidence` (non-empty array of the exact data rows justifying it), `proposal` (object with the exact change, e.g. {"page":"/x","current_title":"...","proposed_title":"..."}), optional `projected_impact` ({metric, current, expected, confidence}).');
  L.push('');
  L.push('## Rules');
  L.push('- Every recommendation you file MUST be backed by real data you fetched — attach the actual rows as evidence. Never fabricate numbers.');
  L.push('- You cannot approve, execute, or deploy anything. The user does that with the buttons in the inbox. After filing, tell the user the recommendation is in the inbox awaiting their approval.');
  L.push('- Max 10 pending per business (the API enforces it) — prefer few high-confidence recommendations over many weak ones.');
  L.push('- Do NOT edit any site files directly; changes go through the recommendation → approval → executor path.');
  return L.join('\n');
}

export function ImproveAssistant({ overview, recs }: { overview: OverviewLite | null; recs: ImproveRecommendation[] }) {
  return (
    <AssistantShell
      slug="improve-asst"
      sessionName="Improve · Assistant"
      sessionIcon="📈"
      title="Improve Assistant"
      subject="the improvement loop"
      emptyGradient="from-cyan-500 to-blue-600"
      emptyBlurb="I can read your GSC data and inbox, explain any recommendation, run analyzers, and file new recommendations for your approval."
      suggestions={SUGGESTIONS}
      buildGrounding={() => buildGrounding(overview, recs)}
      confirmClearText="Clear the Improve assistant chat and start fresh?"
    />
  );
}
